import { useEffect, useState } from "react"
import { DetailPage, Row } from "../../components/crud/DetailPage"
import { Badge, FkLink, ListPage } from "../../components/crud/ListPage"
import { AsyncSelect, Checkbox, DateInput, EmailInput, NumberInput, Select, TextareaInput, TextInput, toDateOnly } from "../../components/forms/Inputs"
import { useOptions } from "../../hooks/useOptions"
import { useTranslations } from "../../layers/Localization"
import { OffreMaterielDetailsList } from "./OffreMaterielDetail"
import { useApi } from "../../hooks/useApi"
import { useParams } from "react-router-dom"
import { HistoriquesList } from "./Historique"
import { useTitleOptions } from "./Contacts"
import { postalCodeToCommune } from "../../hooks/helpers"
import { SendSingleEmailPopup, useStatusEnvoiEmailOptions } from "./Factures"
import { useToday } from "../../hooks/useToday"

const useActions = (setOffre, setSingleEmailPopup, setReload) => {
    const t = useTranslations()
    return {
        [t('Aperçu offre')]: x => window.open(`${process.env.REACT_APP_API_URL}/offreMateriel/${x.id}/pdf`, '_blank').focus(),
        [t('Aperçu bon de livraison')]: x => window.open(`${process.env.REACT_APP_API_URL}/offreMateriel/${x.id}/bonDeLivraison`, '_blank').focus(),
        [t('Envoyer email')]: (item, reload) => {
            setOffre(item)
            setSingleEmailPopup(true)
            setReload(() => reload)
        }
    }
}

function OffreMaterielFields({ item, set, tab }) {
    const [client, setClient] = useState()
    const clientOptions = useOptions('clients', ['nom', 'type'], { query: 'gerant:join=nom' })
    const chantierOptions = useOptions('chantiers', 'nom', { query: client ? `clientId=${client.id}` : '' })
    const contactOptions = useOptions('contacts', ['nom', 'fonction', 'email'], { query: client ? `or2:clientId=${client.id}&or2:gerantId=${client.gerantId}` : '' })
    const personnelOptions = useOptions('personnel', ['prenom', 'nom'], { query: 'signataire=true', label: x => `${x.prenom || ''} ${x.nom || ''} (${x.fonction})` })
    const titreOptions = useTitleOptions()

    const t = useTranslations()
    const api = useApi()
    const { id } = useParams()

    useEffect(() => {
        if (id === 'new') api.get(`/offreMateriel?limit=1&orderBy:desc=num`).then(offres => set('num')((offres[0]?.num || 0) + 1))
    }, [id])

    const onClientChange = (id, item) => {
        set('clientId')(id)
        if (item) {
            set('clientNom')(item.nom)
            set('gerantAdresse')(item.gerant?.adresse)
            set('gerantCp')(item.gerant?.cp)
            set('gerantCommune')(item.gerant?.commune)
        }
    }
    const onContactChange = (id, item) => {
        set('contactId')(id)
        if (item) {
            set('contactTitre')(item.titre)
            set('contactNom')(item.nom)
            set('contactEmail')(item.email)
        }
    }

    const onGerantCodePostalChange = value => {
        set('gerantCp')(value)
        if (!item.gerantCommune && postalCodeToCommune(value)) set('gerantCommune')(postalCodeToCommune(value))
    }

    return <>
        <Row>
            <NumberInput label={t("Numéro")} value={item.num} onChange={set('num')} required max="999999" />
            <DateInput label={t("Date")} value={item.date} onChange={set('date')} required />
        </Row>
        <hr />
        <Row>
            <AsyncSelect label={t("Client")} value={item.clientId} onChange={onClientChange} onItemLoad={setClient} options={clientOptions} required />
            <TextInput className="grow" label={t("Nom")} value={item.clientNom} onChange={set('clientNom')} required />
        </Row>
        <Row>
            <TextInput className="grow" label={t("Adresse gérant")} value={item.gerantAdresse} onChange={set('gerantAdresse')} required />
            <NumberInput label={t("Code postal gérant")} value={item.gerantCp} onChange={onGerantCodePostalChange} min="1000" max="9999" required />
            <TextInput label={t("Commune gérant")} value={item.gerantCommune} onChange={set('gerantCommune')} required />
        </Row>
        <hr />
        <Row>
            <AsyncSelect className="grow" label={t("Chantier")} value={item.chantierId} onChange={set('chantierId')} options={chantierOptions} />
            <AsyncSelect className="grow" label={t("Personne de contact")} value={item.conciergeId} onChange={set('conciergeId')} options={contactOptions} />
        </Row>
        <hr />
        <Row>
            <AsyncSelect label={t("Destinataire")} value={item.contactId} onChange={onContactChange} options={contactOptions} />
        </Row>
        <Row>
            <Select label={t("Titre du destinataire")} value={item.contactTitre} onChange={set('contactTitre')} options={titreOptions} />
            <TextInput className="grow" label={t("Nom du destinataire")} value={item.contactNom} onChange={set('contactNom')} />
            <EmailInput label={t("Email du destinataire")} value={item.contactEmail} onChange={set('contactEmail')} />
        </Row>
        <Checkbox label={t("Afficher total")} value={item.total} onChange={set('total')} />
        <hr />
        <Row>
            <AsyncSelect label={t("Signataire")} value={item.personnelId} onChange={set('personnelId')} options={personnelOptions} required />
        </Row>
        <TextareaInput label={t("Mémo livraison")} value={item.memoInterne} onChange={set('memoInterne')} />
    </>
}

export function OffreMaterielsList(props) {
    const [offre, setOffre] = useState()
    const [singleEmailPopup, setSingleEmailPopup] = useState(false)
    const [reload, setReload] = useState(false)
    const t = useTranslations()
    const actions = useActions(setOffre, setSingleEmailPopup, setReload)
    const statusEnvoiEmailOptions = useStatusEnvoiEmailOptions()

    return <>
        <ListPage model="offreMateriel" title={t("Offres de matériel")} columns={{
            [t('Numéro')]: 'num',
            [t('Date')]: 'date',
            [t('Client')]: { sort: 'client', value: item => <FkLink>{item.client?.referenceCourte}</FkLink> },
            [t('Personne de contact')]: { sort: 'concierge', value: item => <FkLink>{item.concierge?.nom}</FkLink> },
            [t('Signataire')]: { sort: 'personnel', value: item => <FkLink>{`${item.personnel?.prenom} ${item.personnel?.nom || ''}`}</FkLink> },
            [t('Email')]: { sort: 'statusEnvoiEmail', value: item => <Badge value={item?.statusEnvoiEmail} options={statusEnvoiEmailOptions} /> },
        }} sort={{
            [t('Numéro')]: 'desc',
        }} join={{
            client: 'referenceCourte',
            concierge: 'nom',
            personnel: ['prenom', 'nom'],
        }} actions={actions} {...props} />
        <SendSingleEmailPopup value={singleEmailPopup} onChange={setSingleEmailPopup} model="offreMateriel" item={offre} reload={reload} />
    </>
}

export function OffreMaterielDetail(props) {
    const [offre, setOffre] = useState()
    const [singleEmailPopup, setSingleEmailPopup] = useState(false)
    const [reload, setReload] = useState(false)
    const t = useTranslations()
    const actions = useActions(setOffre, setSingleEmailPopup, setReload)
    const today = useToday()

    return <>
        <DetailPage model="offreMateriel" title={t("Offre de matériel")} Fields={OffreMaterielFields} actions={actions} nextKey="num" defaults={{
            date: toDateOnly(new Date(today)),
            total: true,
        }} tables={{
            [t("Détails")]: <OffreMaterielDetailsList relation="offreMaterielId" root="." />,
            [t("Historique")]: <HistoriquesList relation="offreMaterielId" root="." />,
        }} {...props} />
        <SendSingleEmailPopup value={singleEmailPopup} onChange={setSingleEmailPopup} model="offreMateriel" item={offre} reload={reload} />
    </>
}
